import { createTheme, ThemeOptions, Theme, alpha } from '@mui/material/styles';
import { ptBR } from '@mui/material/locale';

declare module '@mui/material/styles' {
  interface Palette {
    gradient: {
      primary: string;
      secondary: string;
    };
  }

  interface PaletteOptions {
    gradient?: {
      primary: string;
      secondary: string;
    };
  }
}

const baseTheme: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: '#0151C4',
      light: '#4285f4',
      dark: '#0d5bdb',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#dc004e',
      light: '#ff5983',
      dark: '#9a0036',
      contrastText: '#ffffff',
    },
    error: {
      main: '#f44336',
      light: '#ef5350',
      dark: '#c62828',
    },
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
    },
    info: {
      main: '#0151C4',
      light: '#4285f4',
      dark: '#0d5bdb',
    },
    success: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.6)',
    },
    gradient: {
      primary: 'linear-gradient(45deg, #0151C4 30%, #4285f4 90%)',
      secondary: 'linear-gradient(45deg, #dc004e 30%, #ff5983 90%)',
    },
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.4,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 500,
      fontSize: '1.25rem',
      lineHeight: 1.5,
    },
    h6: {
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.43,
    },
    button: {
      fontWeight: 500,
      fontSize: '0.875rem',
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
    '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
    '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
    '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
    '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
    '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
    '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
    '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
    '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
    '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
    '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
    '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
    '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
    '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
    '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
    '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
    '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
    '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
        },
      },
    },
  },
};

export const theme = createTheme(baseTheme, ptBR);

/** Paleta escura com tom azulado — evita o visual "cinza chapado" do Material default */
export const darkPaletteOptions: ThemeOptions['palette'] = {
  mode: 'dark',
  primary: {
    main: '#3b82f6',
    light: '#60a5fa',
    dark: '#2563eb',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#818cf8',
    light: '#a5b4fc',
    dark: '#6366f1',
    contrastText: '#ffffff',
  },
  error: {
    main: '#f87171',
    light: '#fca5a5',
    dark: '#ef4444',
  },
  warning: {
    main: '#fbbf24',
    light: '#fcd34d',
    dark: '#f59e0b',
  },
  info: {
    main: '#38bdf8',
    light: '#7dd3fc',
    dark: '#0ea5e9',
  },
  success: {
    main: '#4ade80',
    light: '#86efac',
    dark: '#22c55e',
  },
  background: {
    default: '#070b14',
    paper: '#0d1526',
  },
  text: {
    primary: '#e2e8f0',
    secondary: '#94a3b8',
    disabled: '#64748b',
  },
  divider: 'rgba(59, 130, 246, 0.14)',
  action: {
    active: '#94a3b8',
    hover: 'rgba(59, 130, 246, 0.08)',
    selected: 'rgba(59, 130, 246, 0.16)',
    disabled: 'rgba(148, 163, 184, 0.38)',
    disabledBackground: 'rgba(148, 163, 184, 0.12)',
  },
  grey: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1a2744',
    900: '#111c33',
    A100: '#f5f5f5',
    A200: '#eeeeee',
    A400: '#bdbdbd',
    A700: '#616161',
  },
  gradient: {
    primary: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 50%, #60a5fa 100%)',
    secondary: 'linear-gradient(135deg, #6366f1 0%, #818cf8 50%, #a5b4fc 100%)',
  },
};

export const darkComponentOverrides: ThemeOptions['components'] = {
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        scrollbarColor: '#1e293b transparent',
      },
      '*::-webkit-scrollbar': {
        width: 8,
        height: 8,
      },
      '*::-webkit-scrollbar-thumb': {
        backgroundColor: '#1e293b',
        borderRadius: 4,
      },
      '*::-webkit-scrollbar-thumb:hover': {
        backgroundColor: '#334155',
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: '#0a101f',
        borderRight: '1px solid rgba(59, 130, 246, 0.14)',
        backgroundImage: 'none',
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.45)',
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        border: '1px solid rgba(59, 130, 246, 0.1)',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.28)',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderColor: 'rgba(59, 130, 246, 0.1)',
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(59, 130, 246, 0.2)',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(59, 130, 246, 0.38)',
        },
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        border: '1px solid rgba(59, 130, 246, 0.12)',
        boxShadow: '0 24px 48px rgba(0, 0, 0, 0.5)',
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: '#1a2744',
        border: '1px solid rgba(59, 130, 246, 0.18)',
      },
    },
  },
};

export const darkTheme = createTheme(
  {
    ...baseTheme,
    palette: darkPaletteOptions,
    components: {
      ...baseTheme.components,
      ...darkComponentOverrides,
      MuiButton: {
        ...baseTheme.components?.MuiButton,
        styleOverrides: {
          ...baseTheme.components?.MuiButton?.styleOverrides,
          root: {
            ...(baseTheme.components?.MuiButton?.styleOverrides?.root as object),
            '&:hover': {
              boxShadow: '0 2px 8px rgba(59, 130, 246, 0.25)',
            },
          },
        },
      },
    },
  },
  ptBR,
);

/** Container padrão das páginas de dashboard — respeita light/dark */
export const dashboardPageSx = {
  p: 3,
  minHeight: '100vh',
  bgcolor: 'background.default',
};

/** Cores auxiliares para gráficos Recharts no tema atual */
export function getChartThemeColors(theme: Theme) {
  const isDark = theme.palette.mode === 'dark';
  return {
    grid: isDark ? 'rgba(59, 130, 246, 0.1)' : '#f0f0f0',
    axis: isDark ? '#64748b' : '#9e9e9e',
    radialRemainder: isDark ? 'rgba(59, 130, 246, 0.15)' : '#e0e0e0',
  };
}

/** Estilos Recharts (tooltip, legend, séries) alinhados ao tema */
export function getRechartsStyles(theme: Theme) {
  const isDark = theme.palette.mode === 'dark';
  const chart = getChartThemeColors(theme);
  return {
    ...chart,
    tooltip: {
      contentStyle: {
        backgroundColor: isDark ? '#1a2744' : '#ffffff',
        border: `1px solid ${isDark ? 'rgba(59, 130, 246, 0.25)' : '#e0e0e0'}`,
        borderRadius: 8,
        color: isDark ? '#e2e8f0' : '#333333',
        boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.45)' : '0 4px 12px rgba(0,0,0,0.1)',
      },
      labelStyle: { color: isDark ? '#94a3b8' : '#666666', fontWeight: 600 },
      itemStyle: { color: isDark ? '#e2e8f0' : '#333333' },
    },
    legend: { color: chart.axis },
    series: {
      revenue: theme.palette.success.main,
      expenses: theme.palette.warning.main,
      aReceber: theme.palette.info.main,
      totalTitulos: theme.palette.secondary.main,
      registrations: theme.palette.primary.main,
      cancellations: theme.palette.error.main,
    },
  };
}

/** Card de gráfico do dashboard */
export function dashboardChartCardSx(theme: Theme) {
  const isDark = theme.palette.mode === 'dark';
  return {
    borderRadius: 3,
    ...(isDark && {
      backgroundImage: 'none',
      border: '1px solid rgba(59, 130, 246, 0.12)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.28)',
    }),
  };
}

type PaletteStatColor = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';

/** Card de estatística (KPI) compatível com tema escuro */
export function themedStatCardSx(theme: Theme, colorKey: PaletteStatColor) {
  const color = theme.palette[colorKey].main;
  const isDark = theme.palette.mode === 'dark';
  return {
    bgcolor: isDark ? alpha(color, 0.1) : alpha(color, 0.07),
    border: '1px solid',
    borderColor: isDark ? alpha(color, 0.28) : alpha(color, 0.2),
    borderRadius: 2,
    transition: 'all 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: isDark ? `0 8px 24px ${alpha(color, 0.12)}` : theme.shadows[2],
      borderColor: alpha(color, 0.4),
    },
  };
}

/** Ícone circular dentro de card de estatística */
export function themedStatIconBoxSx(theme: Theme, colorKey: PaletteStatColor) {
  const color = theme.palette[colorKey].main;
  return {
    bgcolor: alpha(color, theme.palette.mode === 'dark' ? 0.2 : 0.14),
    borderRadius: '50%',
    p: 1.5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
}

/** Header paper de páginas de detalhe (cliente, contrato, etc.) */
export function detailPageHeaderPaperSx(theme: Theme) {
  const isDark = theme.palette.mode === 'dark';
  return {
    p: 1.5,
    mb: 1,
    borderRadius: 2,
    border: `1px solid ${isDark ? alpha('#3b82f6', 0.14) : theme.palette.divider}`,
    bgcolor: 'background.paper',
    ...(isDark && {
      backgroundImage: 'none',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.28)',
    }),
  };
}

/** Hero/header com gradiente para páginas de detalhe */
export function detailPageHeroPaperSx(theme: Theme) {
  const isDark = theme.palette.mode === 'dark';
  return {
    overflow: 'hidden',
    borderRadius: 2,
    mb: 3,
    border: `1px solid ${isDark ? alpha('#3b82f6', 0.14) : theme.palette.divider}`,
    background: isDark
      ? `linear-gradient(145deg, #0d1526 0%, #131f38 50%, ${alpha(theme.palette.primary.main, 0.22)} 100%)`
      : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    ...(isDark && { backgroundImage: 'none' }),
  };
}

/** Abas estilo “pastilha” para páginas de detalhe */
export function detailPageTabsSx(theme: Theme) {
  const isDark = theme.palette.mode === 'dark';
  const primary = theme.palette.primary.main;
  return {
    minHeight: 40,
    '& .MuiTabs-scroller': { display: 'flex', justifyContent: 'flex-start' },
    '& .MuiTabs-flexContainer': { justifyContent: 'flex-start' },
    '& .MuiTabs-indicator': { display: 'none' },
    '& .MuiTab-root': {
      minHeight: 40,
      fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
      padding: { xs: '6px 6px', sm: '6px 8px', md: '6px 12px' },
      margin: '4px 2px 0 2px',
      borderRadius: '8px 8px 0 0',
      border: `1px solid ${isDark ? alpha(primary, 0.18) : theme.palette.divider}`,
      backgroundColor: 'transparent',
      color: 'text.secondary',
      transition: 'all 0.2s ease',
      minWidth: 'auto',
      maxWidth: { xs: '120px', sm: '140px', md: '160px' },
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      '&:hover': {
        backgroundColor: alpha(primary, isDark ? 0.12 : 0.06),
        color: 'primary.main',
      },
      '&.Mui-selected': {
        backgroundColor: alpha(primary, isDark ? 0.18 : 0.1),
        color: 'primary.main',
        fontWeight: 600,
        borderColor: alpha(primary, isDark ? 0.35 : 0.25),
      },
    },
  };
}

/** Barra de filtros colapsável */
export function filterBarHeaderSx(theme: Theme) {
  return {
    p: 0.5,
    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.06 : 0.04),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
  };
}

/** Container padrão das páginas de listagem (RankingPage style) */
export const pageShellSx = {
  p: { xs: 1, md: 1.5 },
  mx: 'auto',
};

type PageAccent = 'primary' | 'info' | 'success' | 'warning' | 'secondary';

/** Header com gradiente — padrão RankingPage */
export function rankingHeaderPaperSx(theme: Theme, accent: PageAccent = 'primary') {
  const color = theme.palette[accent].main;
  const isDark = theme.palette.mode === 'dark';
  return {
    mb: 1.5,
    p: { xs: 1.25, md: 1.5 },
    borderRadius: 2,
    background: isDark
      ? `linear-gradient(135deg, ${alpha(color, 0.35)} 0%, ${alpha(theme.palette.primary.main, 0.25)} 50%, ${alpha('#1a1a2e', 0.9)} 100%)`
      : `linear-gradient(135deg, ${alpha(color, 0.12)} 0%, ${alpha(theme.palette.primary.light, 0.15)} 45%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
    overflow: 'hidden',
    position: 'relative' as const,
  };
}

/** Seção de filtros */
export function filtersPaperSx(theme: Theme) {
  return {
    p: { xs: 1.25, md: 1.5 },
    mb: 1.5,
    borderRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.04 : 0.02),
  };
}

/** Paper wrapper da listagem/tabela */
export function listSectionPaperSx(theme: Theme) {
  return {
    borderRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
  };
}

/** Barra de título da listagem */
export function listSectionHeaderBarSx(theme: Theme) {
  return {
    px: 1.5,
    py: 0.75,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    bgcolor: alpha(theme.palette.primary.main, 0.04),
    borderBottom: `1px solid ${theme.palette.divider}`,
  };
}

/** Cabeçalho de tabela estilo RankingPage */
export function rankingTableHeadRowSx(theme: Theme) {
  const primary = theme.palette.primary.main;
  return {
    bgcolor: alpha(primary, 0.06),
    '& .MuiTableCell-head': {
      fontWeight: 700,
      fontSize: '0.68rem',
      textTransform: 'uppercase' as const,
      letterSpacing: 0.5,
      color: 'text.secondary',
      py: 0.75,
      px: 1,
      borderBottom: `2px solid ${alpha(primary, 0.15)}`,
    },
  };
}

/** Wrapper da paginação */
export function tablePaginationWrapperSx(theme: Theme) {
  const primary = theme.palette.primary.main;
  return {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    py: 1.5,
    px: 2,
    bgcolor: alpha(primary, 0.04),
    borderTop: `2px solid ${alpha(primary, 0.2)}`,
  };
}

/** Linha de cabeçalho de tabela temática */
export function tableHeadRowSx(theme: Theme) {
  const primary = theme.palette.primary.main;
  return {
    '& .MuiTableCell-head': {
      bgcolor: `color-mix(in srgb, ${primary} 12%, ${theme.palette.background.paper})`,
      fontWeight: 700,
      fontSize: '0.73rem',
      py: 0.8,
      borderBottom: `2px solid ${alpha(primary, 0.2)}`,
    },
  };
}

/** Container de tabela temático */
export function themedTableContainerSx(theme: Theme) {
  return {
    borderRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
    ...(theme.palette.mode === 'dark' && {
      backgroundImage: 'none',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
    }),
  };
}

/** Accordion de seções em páginas de configuração */
export function configSectionAccordionSx(theme: Theme) {
  const isDark = theme.palette.mode === 'dark';
  return {
    mb: 2,
    borderRadius: '12px !important',
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    bgcolor: 'background.paper',
    ...(isDark && {
      backgroundImage: 'none',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
      '&::before': { display: 'none' },
    }),
    '&.Mui-expanded': {
      margin: '0 0 16px 0',
    },
  };
}

/** Summary do accordion de configuração */
export function configSectionAccordionSummarySx(theme: Theme) {
  const isDark = theme.palette.mode === 'dark';
  const primary = theme.palette.primary.main;
  return {
    minHeight: 56,
    bgcolor: alpha(primary, isDark ? 0.08 : 0.04),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&.Mui-expanded': { minHeight: 56 },
    '& .MuiAccordionSummary-content': {
      my: 1.5,
      '&.Mui-expanded': { my: 1.5 },
    },
  };
}

/** CSS do react-big-calendar alinhado ao tema */
export function getBigCalendarStyles(theme: Theme): string {
  const isDark = theme.palette.mode === 'dark';
  const primary = theme.palette.primary.main;
  const offRangeBg = isDark ? alpha('#3b82f6', 0.04) : '#fafafa';
  const monthBg = isDark ? theme.palette.background.paper : '#ffffff';
  const toolbarColor = isDark ? theme.palette.text.primary : primary;
  const gridBorderColor = isDark ? 'rgba(255, 255, 255, 0.16)' : '#cbd5e1';
  const subSlotBorderColor = isDark ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0';

  return `
    .rbc-calendar { font-family: ${theme.typography.fontFamily}; color: ${theme.palette.text.primary}; }
    .rbc-header {
      padding: 10px 4px;
      font-weight: 700;
      font-size: 0.82rem;
      color: ${theme.palette.text.primary};
      border-bottom: 2px solid ${gridBorderColor};
      border-left: 1px solid ${gridBorderColor};
      background-color: ${isDark ? 'rgba(255, 255, 255, 0.02)' : '#f8fafc'};
    }
    .rbc-month-view, .rbc-time-view, .rbc-agenda-view { border: 1px solid ${gridBorderColor}; border-radius: 8px; overflow: hidden; }
    .rbc-day-bg { background-color: ${monthBg}; }
    .rbc-today { background-color: ${alpha(primary, isDark ? 0.12 : 0.06)}; }
    .rbc-off-range-bg { background-color: ${offRangeBg}; }
    .rbc-date-cell { color: ${theme.palette.text.secondary}; }
    .rbc-date-cell.rbc-now { color: ${primary}; font-weight: 700; }
    .rbc-event { box-shadow: 0 2px 4px rgba(0,0,0,${isDark ? 0.35 : 0.1}); }
    .rbc-toolbar { margin-bottom: 12px; gap: 8px; flex-wrap: wrap; }
    .rbc-toolbar button {
      color: ${toolbarColor};
      border: 1px solid ${alpha(primary, isDark ? 0.35 : 0.5)};
      border-radius: 6px;
      background-color: ${isDark ? alpha(primary, 0.08) : 'transparent'};
    }
    .rbc-toolbar button:active, .rbc-toolbar button.rbc-active {
      background-color: ${primary};
      color: white;
      box-shadow: none;
      border-color: ${primary};
    }
    .rbc-toolbar button:hover {
      background-color: ${alpha(primary, isDark ? 0.18 : 0.1)};
    }
    .rbc-toolbar-label { color: ${theme.palette.text.primary}; font-weight: 700; }
    .rbc-agenda-view table.rbc-agenda-table { border: none; }
    .rbc-agenda-view table.rbc-agenda-table tbody > tr > td {
      padding: 12px 15px;
      vertical-align: middle;
      border-top: 1px solid ${gridBorderColor};
      color: ${theme.palette.text.primary};
    }
    .rbc-agenda-date-cell {
      font-weight: bold;
      color: ${primary} !important;
      white-space: nowrap;
      background-color: transparent !important;
      min-width: 80px;
    }
    .rbc-agenda-time-cell {
      color: ${theme.palette.text.secondary};
      font-variant-numeric: tabular-nums;
      background-color: transparent !important;
    }
    .rbc-agenda-event-cell { font-weight: 500; background-color: transparent !important; }
    .rbc-agenda-date-cell:empty { border-top: none !important; }
    .rbc-time-header, .rbc-time-content { border-color: ${gridBorderColor}; }
    .rbc-time-content > * + * > * { border-left: 1px solid ${gridBorderColor} !important; }
    .rbc-timeslot-group { border-color: ${gridBorderColor}; }
    .rbc-time-slot { border-color: ${subSlotBorderColor}; color: ${theme.palette.text.secondary}; }
    .rbc-time-view .rbc-timeslot-group {
      min-height: 54px !important;
      border-color: ${gridBorderColor} !important;
    }
    .rbc-time-view .rbc-time-slot {
      min-height: 27px !important;
      border-color: ${subSlotBorderColor} !important;
      color: ${theme.palette.text.secondary};
    }
    .rbc-day-slot .rbc-time-slot {
      border-top: 1px solid ${subSlotBorderColor} !important;
    }
    .rbc-time-gutter .rbc-label {
      font-size: 0.76rem;
      font-weight: 600;
      color: ${theme.palette.text.secondary};
      padding: 0 8px;
    }
    .rbc-time-view .rbc-event {
      min-height: 48px !important;
      padding: 3px 5px !important;
      border-radius: 5px !important;
    }
    .rbc-event-content {
      height: 100% !important;
    }
    /* Ocultar linha vazia de "Dia Todo" (All-Day) entre os cabeçalhos de dia e a grade de horários */
    .rbc-time-view .rbc-time-header .rbc-allday-cell,
    .rbc-time-view .rbc-time-header-content > .rbc-allday-cell,
    .rbc-time-view .rbc-time-header-content > .rbc-row:not(.rbc-time-header-cell) {
      display: none !important;
    }
    .rbc-time-header {
      border-bottom: 2px solid ${gridBorderColor};
    }
    .rbc-time-content {
      border-top: none !important;
    }
    .rbc-current-time-indicator { background-color: ${primary}; }
    .rbc-addons-dnd .rbc-addons-dnd-over {
      background-color: ${alpha(primary, 0.18)} !important;
      border: 1px dashed ${primary} !important;
    }
    .rbc-addons-dnd .rbc-addons-dnd-drag-preview {
      box-shadow: 0 8px 24px rgba(0,0,0,0.3) !important;
      transform: scale(1.02);
      border-radius: 6px !important;
      z-index: 9999 !important;
    }
    .rbc-event { cursor: grab !important; }
    .rbc-event:active { cursor: grabbing !important; }
    .rbc-time-view .rbc-event-label { display: none !important; }
  `;
}

/** Fundo do AppBar — gradiente por tema */
export function getHeaderAppBarBackground(
  theme: Theme,
  themeId: 'default' | 'dark' | 'copa' | 'rosa' | 'usa',
): string {
  if (themeId === 'dark') {
    return 'linear-gradient(135deg, #0a101f 0%, #111c33 45%, #1a2f52 100%)';
  }
  if (themeId === 'copa') {
    return '#ffffff';
  }
  return theme.palette.primary.main;
} 